export type Category = 'server' | 'domain' | 'software' | 'other';
export type Currency = 'CNY' | 'USD' | 'EUR' | 'HKD';
export type Cycle = 'monthly' | 'quarterly' | 'halfyear' | 'yearly' | 'biennial' | 'once';
export interface Subscription {
  id: string;
  name: string;
  provider: string;
  category: Category;
  price: number;
  currency: Currency;
  cycle: Cycle;
  paymentDay: number;
  startDate: string;
  nextPayment: string;
  expiryDate: string;
  expiryAnchorDay?: number;
  autoRenew: boolean;
  remindDays: number;
  status: 'active' | 'paused';
  resource: string;
  url: string;
  note: string;
  tags: string[];
  color: string;
  isDemo: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface Payment {
  id: string;
  subscriptionId: string;
  name: string;
  date: string;
  amount: number;
  currency: Currency;
  note: string;
  previousNextPayment: string;
  nextPayment: string;
  previousExpiry: string;
  expiryDate: string;
  previousStatus: 'active' | 'paused';
  createdAt: string;
}
export interface Settings {
  workspaceName: string;
  monthlyBudget: number;
  rates: Record<Currency, number>;
}
export interface AccessInfo {
  deployment: 'local' | 'server';
  publicUrl: string;
  isLocal: boolean;
  passwordEnabled: boolean;
  authenticated: boolean;
  addresses: string[];
}
export interface AppData {
  subscriptions: Subscription[];
  payments: Payment[];
  settings: Settings;
  today: string;
  access: AccessInfo;
}
export const categories: Record<Category, { label: string; color: string }> = {
  server: { label: '服务器', color: '#6484de' },
  domain: { label: '域名', color: '#20a58c' },
  software: { label: '软件与服务', color: '#a18ad1' },
  other: { label: '其他', color: '#e5a650' },
};
export const cycles: Record<Cycle, { label: string; short: string; months: number }> = {
  monthly: { label: '每月', short: '月', months: 1 },
  quarterly: { label: '每季度', short: '季', months: 3 },
  halfyear: { label: '每半年', short: '半年', months: 6 },
  yearly: { label: '每年', short: '年', months: 12 },
  biennial: { label: '每两年', short: '两年', months: 24 },
  once: { label: '一次性', short: '次', months: 0 },
};
export const currencySymbols: Record<Currency, string> = {
  CNY: '¥',
  USD: '$',
  EUR: '€',
  HKD: 'HK$',
};
export const defaultSettings: Settings = {
  workspaceName: '我的工作空间',
  monthlyBudget: 500,
  rates: { CNY: 1, USD: 7.2, EUR: 7.8, HKD: 0.92 },
};

/** Date-only arithmetic always uses UTC; the reference day is in Asia/Shanghai. */
export function todayString(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (name: string) => parts.find((p) => p.type === name)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}
export function validDate(date: unknown): date is string {
  if (
    typeof date !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    date < '1900-01-01' ||
    date > '2200-12-31'
  )
    return false;
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date;
}
export function addDays(date: string, days: number): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}
export function addMonths(date: string, months: number, anchorDay = Number(date.slice(8))): string {
  const [year, month] = date.split('-').map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(anchorDay, lastDay));
  return target.toISOString().slice(0, 10);
}
export function nextPaymentDate(
  sub: Pick<Subscription, 'nextPayment' | 'cycle' | 'paymentDay'>,
): string {
  return cycles[sub.cycle].months
    ? addMonths(sub.nextPayment, cycles[sub.cycle].months, sub.paymentDay)
    : '';
}
export function monthlyCost(sub: Subscription, rates: Settings['rates']): number {
  return sub.status === 'active' && cycles[sub.cycle].months
    ? (sub.price * rates[sub.currency]) / cycles[sub.cycle].months
    : 0;
}
export function relevantDate(sub: Subscription): string {
  return [sub.nextPayment, sub.expiryDate].filter(Boolean).sort()[0] || '';
}
export function attention(
  sub: Subscription,
  today: string,
): { days: number; kind: 'expired' | 'urgent' | 'soon' | 'safe' | 'paused'; label: string } {
  if (sub.status === 'paused') return { days: Infinity, kind: 'paused', label: '已停用' };
  const date = relevantDate(sub);
  const days = date ? daysBetween(today, date) : Infinity;
  if (days < 0) return { days, kind: 'expired', label: `已逾期 ${Math.abs(days)} 天` };
  if (days === 0) return { days, kind: 'urgent', label: '今天到期' };
  if (days <= Math.min(7, sub.remindDays)) return { days, kind: 'urgent', label: `${days} 天后` };
  if (days <= sub.remindDays) return { days, kind: 'soon', label: `${days} 天后` };
  return { days, kind: 'safe', label: Number.isFinite(days) ? `${days} 天后` : '长期有效' };
}
export interface CalendarEvent {
  id: string;
  subscription: Subscription;
  date: string;
  type: 'payment' | 'expiry';
  amount: number;
}
/** Project future bills without claiming that an overdue bill has been paid. */
export function calendarEvents(subs: Subscription[], from: string, to: string): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  for (const sub of subs.filter((s) => s.status === 'active')) {
    let date = sub.nextPayment;
    const months = cycles[sub.cycle].months;
    if (date && months && date < from) {
      const difference =
        (Number(from.slice(0, 4)) - Number(date.slice(0, 4))) * 12 +
        Number(from.slice(5, 7)) -
        Number(date.slice(5, 7));
      date = addMonths(date, Math.max(0, Math.floor(difference / months)) * months, sub.paymentDay);
    }
    for (let i = 0; date && date <= to && i < 400; i++) {
      if (date >= from && date >= sub.startDate)
        events.push({
          id: `${sub.id}-payment-${date}`,
          subscription: sub,
          date,
          type: 'payment',
          amount: sub.price,
        });
      if (!months) break;
      date = addMonths(date, months, sub.paymentDay);
    }
    if (sub.expiryDate && sub.expiryDate >= from && sub.expiryDate <= to)
      events.push({
        id: `${sub.id}-expiry-${sub.expiryDate}`,
        subscription: sub,
        date: sub.expiryDate,
        type: 'expiry',
        amount: 0,
      });
  }
  return events.sort(
    (a, b) =>
      a.date.localeCompare(b.date) || a.subscription.name.localeCompare(b.subscription.name),
  );
}
export function money(amount: number, currency: Currency = 'CNY', decimals = 2): string {
  return `${currencySymbols[currency]}${amount.toLocaleString('zh-CN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}
export function shortDate(date: string): string {
  return date ? `${Number(date.slice(5, 7))} 月 ${Number(date.slice(8))} 日` : '—';
}
export function fullDate(date: string): string {
  return date ? date.replaceAll('-', '.') : '—';
}

function textField(value: unknown, label: string, max: number, required = false): string {
  if (value != null && typeof value !== 'string') throw new Error(`${label}格式不正确`);
  const result = ((value as string) || '').trim();
  if (required && !result) throw new Error(`请填写${label}`);
  if (result.length > max) throw new Error(`${label}不能超过 ${max} 个字`);
  return result;
}
export function validateSubscription(
  input: unknown,
): Omit<Subscription, 'id' | 'isDemo' | 'createdAt' | 'updatedAt'> {
  if (!input || typeof input !== 'object' || Array.isArray(input))
    throw new Error('订阅数据格式不正确');
  const s = input as Record<string, unknown>;
  if (!Object.hasOwn(categories, String(s.category))) throw new Error('请选择有效分类');
  if (!Object.hasOwn(cycles, String(s.cycle))) throw new Error('请选择有效付款周期');
  if (!Object.hasOwn(currencySymbols, String(s.currency))) throw new Error('请选择有效币种');
  if (
    typeof s.price !== 'number' ||
    !Number.isFinite(s.price) ||
    s.price < 0 ||
    s.price > 100000000
  )
    throw new Error('金额应为 0 到 100,000,000');
  if (!validDate(s.startDate)) throw new Error('开始日期不正确');
  const nextPayment = textField(s.nextPayment, '付款日期', 10);
  const expiryDate = textField(s.expiryDate, '到期日期', 10);
  if (nextPayment && (!validDate(nextPayment) || nextPayment < s.startDate))
    throw new Error('付款日期必须为有效日期，且不早于开始日期');
  if (s.status !== 'paused' && !nextPayment) throw new Error('启用的订阅必须填写下次付款日期');
  if (expiryDate && (!validDate(expiryDate) || expiryDate < s.startDate))
    throw new Error('到期日期必须为有效日期，且不早于开始日期');
  const paymentDay = s.paymentDay;
  if (
    typeof paymentDay !== 'number' ||
    !Number.isInteger(paymentDay) ||
    paymentDay < 1 ||
    paymentDay > 31
  )
    throw new Error('固定付款日应为 1 到 31');
  const remindDays = s.remindDays;
  if (
    typeof remindDays !== 'number' ||
    !Number.isInteger(remindDays) ||
    remindDays < 0 ||
    remindDays > 365
  )
    throw new Error('提醒天数应为 0 到 365');
  if (typeof s.autoRenew !== 'boolean') throw new Error('续费方式不正确');
  if (
    s.expiryAnchorDay !== undefined &&
    (typeof s.expiryAnchorDay !== 'number' ||
      !Number.isInteger(s.expiryAnchorDay) ||
      s.expiryAnchorDay < 1 ||
      s.expiryAnchorDay > 31)
  )
    throw new Error('资源到期日锚点无效');
  if (!['active', 'paused'].includes(String(s.status))) throw new Error('订阅状态不正确');
  const url = textField(s.url, '管理链接', 1000);
  if (url) {
    try {
      if (!['http:', 'https:'].includes(new URL(url).protocol)) throw new Error();
    } catch {
      throw new Error('管理链接应以 https:// 或 http:// 开头');
    }
  }
  if (!Array.isArray(s.tags) || s.tags.length > 8) throw new Error('标签最多 8 个');
  const color = /^#[a-fA-F0-9]{6}$/.test(String(s.color)) ? String(s.color) : '#14866e';
  return {
    name: textField(s.name, '订阅名称', 100, true),
    provider: textField(s.provider, '服务商', 100),
    category: s.category as Category,
    price: Math.round(s.price * 100) / 100,
    currency: s.currency as Currency,
    cycle: s.cycle as Cycle,
    paymentDay,
    startDate: s.startDate,
    nextPayment,
    expiryDate,
    expiryAnchorDay: (s.expiryAnchorDay as number) || Number(expiryDate.slice(8)) || 1,
    autoRenew: s.autoRenew,
    remindDays,
    status: s.status as 'active' | 'paused',
    resource: textField(s.resource, '资源信息', 300),
    url,
    note: textField(s.note, '备注', 3000),
    tags: [...new Set(s.tags.map((t) => textField(t, '标签', 30, true)))],
    color,
  };
}
export function validateSettings(input: unknown): Settings {
  if (!input || typeof input !== 'object') throw new Error('设置格式不正确');
  const s = input as Settings;
  if (
    typeof s.monthlyBudget !== 'number' ||
    !Number.isFinite(s.monthlyBudget) ||
    s.monthlyBudget < 0 ||
    s.monthlyBudget > 100000000
  )
    throw new Error('预算金额不正确');
  const rates = {} as Settings['rates'];
  for (const key of Object.keys(currencySymbols) as Currency[]) {
    const value = s.rates?.[key];
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0 || value > 100000)
      throw new Error(`${key} 汇率应大于 0`);
    rates[key] = key === 'CNY' ? 1 : value;
  }
  return {
    workspaceName: textField(s.workspaceName, '工作空间名称', 40, true),
    monthlyBudget: s.monthlyBudget,
    rates,
  };
}
